import apiClient from '@/api/axiosClient';
import type { AxiosError } from 'axios';
import type {
  PrimaryTypeChangeRequest,
  PrimaryTypeChangeResponse
} from '@/api/contracts/tableDefinition';
import type {
  ColumnDefinitionUpdateRequest,
  ColumnDefaultFunction,
  NewColumnRequest,
  NullHandling,
  RequiredApplyRequest,
  RequiredPreflightRequest,
  RequiredPreflightResponse
} from '@/api/contracts/tableColumns';
import type {
  FromSchemaOrFallback,
  UpdateLengthRequestFallback,
  SetNullRequestFallback,
  SetNotNullRequestFallback
} from '@/api/types/schema-fallbacks';

export type { NewColumnRequest, NullHandling, ColumnDefaultFunction };
export type UpdateColumnDefinitionRequest = ColumnDefinitionUpdateRequest;
export type DefaultFunction = ColumnDefaultFunction;

export interface AddColumnPreflightResult {
  rowCount: number;
  needsFillValue: boolean;
}

export interface AddColumnResult {
  success: boolean;
  errors?: Record<string, string[]>;
}

/**
 * Legg til ny kolonne i en tabell
 * Returnerer { success: true } ved suksess, eller { success: false, errors: {...} } ved valideringsfeil
 */
export async function addTableColumn (
  tableKey: string,
  request: NewColumnRequest,
  signal?: AbortSignal
): Promise<AddColumnResult> {
  try {
    await apiClient.post(`v2/table/column/${tableKey}`, request, { signal });
    return { success: true };
  } catch (error) {
    const axiosError = error as AxiosError<{ errors?: Record<string, string[]>; detail?: string; title?: string }>;
    if (axiosError.response?.status === 400) {
      // Håndter både FluentValidation errors-format og ProblemDetails-format
      if (axiosError.response.data?.errors) {
        return { success: false, errors: axiosError.response.data.errors };
      }
      // ProblemDetails format (title/detail)
      const detail = axiosError.response.data?.detail || axiosError.response.data?.title || 'Ukjent valideringsfeil';
      return { success: false, errors: { general: [detail] } };
    }
    throw error;
  }
}

/**
 * Preflight for AddColumn - sjekker om tabellen har data
 */
export async function addColumnPreflight (
  tableKey: string,
  nullHandling: string,
  signal?: AbortSignal
): Promise<AddColumnPreflightResult> {
  const response = await apiClient.get<AddColumnPreflightResult>(
    `v2/table/column/${tableKey}/preflight`,
    { params: { nullHandling }, signal }
  );
  return response.data;
}

/**
 * Slett en kolonne fra en tabell
 */
export async function deleteTableColumn (
  tableKey: string,
  columnName: string,
  signal?: AbortSignal
): Promise<void> {
  await apiClient.delete(`v2/table/column/${tableKey}`, {
    params: { columnName },
    signal
  });
}

// ===== Column modification operations =====
export type UpdateLengthRequest = FromSchemaOrFallback<'UpdateLengthRequest', UpdateLengthRequestFallback>;
export type SetNullRequest = FromSchemaOrFallback<'SetNullRequest', SetNullRequestFallback>;
export type SetNotNullRequest = FromSchemaOrFallback<'SetNotNullRequest', SetNotNullRequestFallback>;
export type { RequiredPreflightRequest, RequiredApplyRequest, RequiredPreflightResponse };

/**
 * Oppdater kolonnelengde
 */
export async function updateColumnLength (
  request: UpdateLengthRequest,
  signal?: AbortSignal
): Promise<void> {
  await apiClient.post('v2/table/column/update-length', request, { signal });
}

/**
 * Sett kolonne til nullable
 */
export async function setColumnNull (
  request: SetNullRequest,
  signal?: AbortSignal
): Promise<void> {
  await apiClient.post('v2/table/column/set-null', request, { signal });
}

/**
 * Sett kolonne til NOT NULL
 */
export async function setColumnNotNull (
  request: SetNotNullRequest,
  signal?: AbortSignal
): Promise<void> {
  await apiClient.post('v2/table/column/set-not-null', request, { signal });
}

/**
 * Preflight for required column - sjekker om tabellen har data som trenger fill-verdi
 */
export async function requiredColumnPreflight (
  request: RequiredPreflightRequest,
  signal?: AbortSignal
): Promise<RequiredPreflightResponse> {
  const response = await apiClient.post<RequiredPreflightResponse>('v2/table/column/required/preflight', request, { signal });
  return response.data;
}

/**
 * Sett kolonne som required med fill-verdi for eksisterende rader
 */
export async function requiredColumnApply (
  request: RequiredApplyRequest,
  signal?: AbortSignal
): Promise<void> {
  await apiClient.post('v2/table/column/required/apply', request, { signal });
}

/**
 * Utfør primærtype-endring for en kolonne
 */
export async function changePrimaryType (
  tableKey: string,
  columnName: string,
  request: PrimaryTypeChangeRequest
): Promise<PrimaryTypeChangeResponse> {
  const response = await apiClient.put<PrimaryTypeChangeResponse>(
    `v2/table/column/${tableKey}/${columnName}/primary-type`,
    request
  );
  return response.data;
}

/**
 * Oppdater kun kolonnens label (visningsnavn)
 * Bruker dedikert endpoint som ikke krever full payload
 */
export async function updateColumnLabel (
  tableKey: string,
  columnName: string,
  newLabel: string,
  signal?: AbortSignal
): Promise<void> {
  await apiClient.put(
    'v2/table/column/label',
    null,
    { params: { tableKey, columnName, newValue: newLabel }, signal }
  );
}

